import { limitString } from "src/Common/function";
import { MdEdit, MdDeleteOutline } from "react-icons/md";
import { Oval } from "react-loader-spinner";

const HeaderCell = ({ children, className }) => (
  <th
    className={`text-left font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477] px-6 py-3 ${className || ""}`}
  >
    {children}
  </th>
);

const ReportTable = ({ reports = [], onEditClicked, onDeleteClicked, isLoading = false }) => {
  return (
    <div className="w-full bg-white shadow-sm border-[1px] border-[#E5E8F3] rounded-[12px] overflow-hidden">
      <table className="w-full border-collapse">
        <thead className="bg-[#F8F9FE]">
          <tr className="border-b-[1px] border-[#E5E8F3]">
            <HeaderCell>Report Type</HeaderCell>
            <HeaderCell>Technicians</HeaderCell>
            <HeaderCell>Recipients</HeaderCell>
            <HeaderCell>Schedule</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell className="w-[100px]" />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} className="px-6">
                <div className="flex items-center justify-center min-h-[calc(100vh-320px)]">
                  <Oval visible height="40" width="40" color="#0065FD" strokeWidth={4} secondaryColor="#E5E8F3" />
                </div>
              </td>
            </tr>
          ) : reports.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6">
                <div className="flex items-center justify-center min-h-[calc(100vh-320px)] font-OpenSansRegular text-[15px] text-[#5F6477]">
                  No scheduled reports yet.
                </div>
              </td>
            </tr>
          ) : (
            reports.map((report, index) => (
              <tr key={report.id ?? index} className="h-[72px] border-b-[1px] border-[#E5E8F3]">
                <td className="px-6 py-4 font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">
                  {report.type}
                </td>
                <td className="px-6 py-4 font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                  {limitString(report.technicians, 36) || "—"}
                </td>
                <td className="px-6 py-4 font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                  {limitString(report.recipients, 36) || "—"}
                </td>
                <td className="px-6 py-4">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">{report.cycleLabel}</p>
                  <p className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                    {report.scheduleDetail}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-[16px] bg-[#DCFCE7] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#15803D]">
                    {report.status || "Active"}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onEditClicked(index)}
                      className="p-2.5 rounded-[8px] text-[#5F6477] hover:bg-[#F8F9FE]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <g clip-path="url(#clip0_5974_19395)">
                          <path
                            d="M14.167 2.5009C14.3859 2.28203 14.6457 2.10842 14.9317 1.98996C15.2176 1.87151 15.5241 1.81055 15.8337 1.81055C16.1432 1.81055 16.4497 1.87151 16.7357 1.98996C17.0216 2.10842 17.2815 2.28203 17.5003 2.5009C17.7192 2.71977 17.8928 2.97961 18.0113 3.26558C18.1297 3.55154 18.1907 3.85804 18.1907 4.16757C18.1907 4.4771 18.1297 4.7836 18.0113 5.06956C17.8928 5.35553 17.7192 5.61537 17.5003 5.83424L6.25033 17.0842L1.66699 18.3342L2.91699 13.7509L14.167 2.5009Z"
                            stroke="#5F6477"
                            stroke-width="1.66667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_5974_19395">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteClicked(index)}
                      className="p-2.5 rounded-[8px] text-[#5F6477] hover:bg-[#F8F9FE]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M2.5 4.99935H4.16667M4.16667 4.99935H17.5M4.16667 4.99935V16.666C4.16667 17.108 4.34226 17.532 4.65482 17.8445C4.96738 18.1571 5.39131 18.3327 5.83333 18.3327H14.1667C14.6087 18.3327 15.0326 18.1571 15.3452 17.8445C15.6577 17.532 15.8333 17.108 15.8333 16.666V4.99935H4.16667ZM6.66667 4.99935V3.33268C6.66667 2.89065 6.84226 2.46673 7.15482 2.15417C7.46738 1.84161 7.89131 1.66602 8.33333 1.66602H11.6667C12.1087 1.66602 12.5326 1.84161 12.8452 2.15417C13.1577 2.46673 13.3333 2.89065 13.3333 3.33268V4.99935M8.33333 9.16602V14.166M11.6667 9.16602V14.166"
                          stroke="#5F6477"
                          stroke-width="1.66667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ReportTable;
